<template>
    <div class="attr-audio">
        <span class="attr-label">{{ value.label }}</span>

        <div v-if="tracks.length" class="attr-audio-list">
            <div v-for="track in tracks" :key="track.key" class="attr-audio-player">
                <div class="attr-audio-icon">
                    <i class="fal fa-microphone"></i>
                </div>
                <div class="attr-audio-main">
                    <audio
                        v-show="!failed[track.key]"
                        :src="track.src"
                        controls
                        preload="metadata"
                        class="attr-audio-el"
                        @error="onError(track)"
                    ></audio>
                    <span v-if="failed[track.key]" class="attr-audio-error">
                        {{ $t('post.page.attachment.audio_unavailable') }}
                    </span>
                    <span v-if="track.name" class="attr-audio-name">{{ track.name }}</span>
                </div>
                <a
                    v-if="track.downloadUrl"
                    :href="track.downloadUrl"
                    :download="track.name"
                    class="attr-audio-dl"
                    :title="$t('post.page.attachment.download')"
                >
                    <i class="fal fa-download text-white"></i>
                </a>
            </div>
        </div>

        <div v-else class="attr-audio-empty">
            <i class="fal fa-microphone-slash"></i>
            <span>{{ $t('post.page.attachment.no_recording') }}</span>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
export default {
    name: 'AudioAttribute',
    props: ['value', 'medias'],
    data() {
        return {
            failed: {}
        }
    },
    computed: {
        // The answer holds one or several media ids — an edited answer may
        // carry objects ({ id }), an imported one a ready-made URL.
        tracks() {
            const raw = this.value && this.value.value
            if (!raw) return []
            const entries = Array.isArray(raw) ? raw : [raw]
            return entries
                .map((entry) => this.buildTrack(entry))
                .filter((track) => track && track.src)
        }
    },
    methods: {
        buildTrack(entry) {
            if (!entry) return null
            const baseUrl = this.$store.state.baseURL
            const token = this.$store.state.auth.token

            // Already a URL (absolute or root-relative)
            if (typeof entry === 'string' && /^(https?:|blob:|data:|\/)/.test(entry)) {
                return { key: entry, src: entry, name: null, downloadUrl: null }
            }

            const id = entry && entry.id ? entry.id : entry
            const media = (this.medias || []).find((m) => m.id == id)
            if (!media) return null

            return {
                key: media.id,
                name: media.name,
                src: `${baseUrl}/storage/${media.id}?token=${token}`,
                downloadUrl: `${baseUrl}/storage/download/${media.id}?token=${token}`
            }
        },
        onError(track) {
            this.$set(this.failed, track.key, true)
        }
    }
}
</script>

<style scoped>
.attr-label {
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
    margin-bottom: 8px;
}

.attr-audio-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.attr-audio-player {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
}

.attr-audio-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #ecfeff;
    color: #0891b2;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
}

.attr-audio-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.attr-audio-el {
    width: 100%;
    height: 36px;
}

.attr-audio-error {
    font-size: 0.8rem;
    color: #b91c1c;
}

.attr-audio-name {
    font-size: 0.72rem;
    color: #94a3b8;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.attr-audio-dl {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #0891b2;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.2s;
}

.attr-audio-dl:hover {
    background: #0e7490;
}

.attr-audio-dl i {
    color: #fff !important;
    font-size: 0.82rem;
}

.attr-audio-empty {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #94a3b8;
    font-size: 0.85rem;
    font-style: italic;
}

.attr-audio-empty i {
    font-size: 1rem;
}

@media (max-width: 640px) {
    .attr-audio-player {
        padding: 10px 12px;
        gap: 8px;
    }

    .attr-audio-icon {
        display: none;
    }
}
</style>
